import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Dimensions, Image, Platform } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation, useRoute } from '@react-navigation/native';

const Header = ({ title, showBackButton = false, onMenuPress }) => {
  const { colors, isDarkMode, toggleTheme } = useTheme();
  const { session, profile, openAuthModal, signOut } = useAuth();
  const navigation = useNavigation();
  const route = useRoute();
  const currentRoute = route?.name;

  // Get screen dimensions for responsive behavior
  const screenWidth = Dimensions.get('window').width;
  const isMobile = screenWidth < 768;

  const navigationItems = [
    { name: 'Home', screen: 'Home' },
    { name: 'Services', screen: 'GSTFiling' },
    { name: 'Calculators', screen: 'CalculatorsHome' },
    { name: 'Tools', screen: 'ToolsHome' },
    { name: 'Resources', screen: 'Resources', externalUrl: 'https://blog.modernmunshiji.com/' },
    { name: 'Contact', screen: 'Contact' },
  ];

  const calculatorRoutes = new Set([
    'CalculatorsHome',
    'IncomeTaxCalculator', 'TDSCalculator', 'GSTCalculator', 'EMICalculator', 'HomeLoanEMICalculator', 'CarLoanEMICalculator', 'PersonalLoanEMICalculator', 'BusinessLoanEMICalculator', 'DownPaymentCalculator', 'SWPCalculator', 'MutualFundReturnsCalculator', 'FDCalculator', 'RDCalculator', 'LumpsumCalculator', 'HRACalculator', 'CapitalGainsCalculator', 'AdvanceTaxCalculator', 'SimpleInterestCalculator', 'SIPCalculator', 'SalaryCalculator', 'PFCalculator', 'PPFCalculator', 'GratuityCalculator', 'SSYCalculator', 'NPSCalculator', 'RetirementCalculator', 'CompoundInterestCalculator', 'NSCCalculator', 'StepUpSIPCalculator', 'APYCalculator', 'CAGRCalculator', 'FlatVsReducingRateCalculator', 'BrokerageCalculator', 'MarginCalculator', 'InflationCalculator', 'PostOfficeMISCalculator', 'SCSSCalculator', 'StockAverageCalculator', 'XIRRCalculator'
  ]);
  const toolRoutes = new Set(['ToolsHome', 'InvoiceGenerator', 'QuotationMaker']);
  const serviceRoutes = new Set(['GSTFiling', 'ITRFiling', 'BusinessRegistration', 'RTOServices']);

  const styles = StyleSheet.create({
    header: {
      backgroundColor: colors.background,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      paddingHorizontal: 20,
      paddingVertical: 15,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 60,
      position: 'relative',
      // Ensure header and its dropdown menus stack above page hero sections
      zIndex: 10000,
    },
    leftSection: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    hamburgerButton: {
      padding: 8,
      marginRight: 15,
      borderRadius: 4,
    },
    hamburgerIcon: {
      fontSize: 20,
      color: colors.text,
    },
    backButton: {
      marginRight: 15,
      padding: 5,
    },
    backText: {
      fontSize: 18,
      color: colors.primary,
    },
    logo: {
      height: 40,
      width: 160,
      resizeMode: 'contain',
      marginRight: 30,
    },
    centerSection: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'box-none',
    },
    centerNavigation: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0,
    },
    navigation: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    navItem: {
      paddingHorizontal: 15,
      paddingVertical: 8,
      marginRight: 10,
      borderRadius: 6,
    },
    activeNavItem: {
      backgroundColor: colors.primary + '15',
    },
    navText: {
      fontSize: 14,
      color: colors.text,
      fontWeight: '500',
    },
    activeNavText: {
      color: colors.primary,
    },
    rightSection: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    authButton: {
      marginLeft: 16,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 8,
      backgroundColor: colors.primary,
    },
    authButtonText: {
      color: '#fff',
      fontWeight: '600',
      fontSize: 14,
    },
    themeToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      marginLeft: 20,
    },
    themeLabel: {
      fontSize: 12,
      color: colors.textSecondary,
      marginRight: 8,
    },
    toggleButton: {
      width: 40,
      height: 22,
      borderRadius: 11,
      backgroundColor: isDarkMode ? colors.primary : colors.border,
      justifyContent: 'center',
      paddingHorizontal: 2,
    },
    toggleCircle: {
      width: 18,
      height: 18,
      borderRadius: 9,
      backgroundColor: colors.background,
      alignSelf: isDarkMode ? 'flex-end' : 'flex-start',
    },
    dropdown: {
      position: 'absolute',
      top: 40,
      left: -20,
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      minWidth: 240,
      zIndex: 10001,
      paddingVertical: 6,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      elevation: 6,
    },
    dropdownItem: {
      paddingHorizontal: 12,
      paddingVertical: 10,
    },
    dropdownItemText: {
      fontSize: 14,
      color: colors.text,
    },
    dropdownFooter: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingHorizontal: 12,
      paddingVertical: 10,
    },
    dropdownFooterText: {
      fontSize: 13,
      color: colors.primary,
      fontWeight: '600',
    },
  });

  return (
    <View style={styles.header}>
      <View style={styles.leftSection}>
        {onMenuPress && (
          <TouchableOpacity
            style={styles.hamburgerButton}
            onPress={onMenuPress}
          >
            <Text style={styles.hamburgerIcon}>☰</Text>
          </TouchableOpacity>
        )}

        {showBackButton && (
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backText}>←</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          onPress={() => navigation.navigate('Home')}
          style={{ cursor: 'pointer' }}
          accessibilityRole="link"
          accessibilityLabel="Go to homepage"
        >
          <Image
            source={require('../../assets/Modern-Munishiji-Logo-Transparent.png')}
            style={styles.logo}
          />
        </TouchableOpacity>

        {false && (
          <View style={styles.navigation}>
            {navigationItems.map((item, index) => (
              <TouchableOpacity
                key={item.name}
                style={[
                  styles.navItem,
                  index === 0 && styles.activeNavItem // Home is active by default
                ]}
                onPress={() => navigation.navigate(item.screen)}
              >
                <Text style={[
                  styles.navText,
                  index === 0 && styles.activeNavText
                ]}>
                  {item.name}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>

      <View style={styles.centerSection}>
        {!isMobile && (
          <View style={styles.centerNavigation}>
            {navigationItems.map((item) => {
              const isActive = (
                item.screen === 'Home' ? currentRoute === 'Home' :
                  item.screen === 'CalculatorsHome' ? calculatorRoutes.has(currentRoute) :
                    item.screen === 'ToolsHome' ? toolRoutes.has(currentRoute) :
                      item.screen === 'GSTFiling' ? serviceRoutes.has(currentRoute) :
                        currentRoute === item.screen
              );
              return (
                <NavWithDropdown
                  key={item.name}
                  item={item}
                  isActive={isActive}
                  navigation={navigation}
                  styles={styles}
                />
              );
            })}
          </View>
        )}
      </View>

      <View style={styles.rightSection}>
        {!session ? (
          <TouchableOpacity style={styles.authButton} onPress={openAuthModal}>
            <Text style={styles.authButtonText}>Login/Signup</Text>
          </TouchableOpacity>
        ) : (
          <UserMenu onDashboard={() => navigation.navigate('Dashboard')} />
        )}
      </View>
    </View>
  );
};

export default Header;

const NavWithDropdown = ({ item, isActive, navigation, styles }) => {
  const { colors } = useTheme();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const closeTimer = React.useRef(null);

  React.useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handleClick = (e) => {
      if (!rootRef.current) return;
      const el = rootRef.current;
      if (el && !el.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick, true);
    return () => document.removeEventListener('mousedown', handleClick, true);
  }, []);

  React.useEffect(() => () => { if (closeTimer.current) { try { clearTimeout(closeTimer.current); } catch (_) { } } }, []);

  const calculatorsMenu = [
    { name: 'EMI Calculator', screen: 'EMICalculator' },
    { name: 'Income Tax Calculator', screen: 'IncomeTaxCalculator' },
    { name: 'SIP Calculator', screen: 'SIPCalculator' },
    { name: 'FD Calculator', screen: 'FDCalculator' },
    { name: 'GST Calculator', screen: 'GSTCalculator' },
  ];
  const toolsMenu = [
    { name: 'Invoice Generator', screen: 'InvoiceGenerator' },
    { name: 'Quotation Maker', screen: 'QuotationMaker' },
  ];
  const servicesMenu = [
    { name: 'GST Filing', screen: 'GSTFiling' },
    { name: 'ITR Filing', screen: 'ITRFiling' },
    { name: 'Business Registration', screen: 'BusinessRegistration' },
    { name: 'RTO Services', screen: 'RTOServices' },
  ];

  const isDropdown = item.screen === 'CalculatorsHome' || item.screen === 'ToolsHome' || item.screen === 'GSTFiling';
  const dropdownItems = item.screen === 'CalculatorsHome' ? calculatorsMenu : item.screen === 'GSTFiling' ? servicesMenu : toolsMenu;

  const handleMouseEnter = () => {
    if (Platform.OS !== 'web' || !isDropdown) return;
    if (closeTimer.current) { try { clearTimeout(closeTimer.current); } catch (_) { } }
    setOpen(true);
  };
  const handleMouseLeave = () => {
    if (Platform.OS !== 'web' || !isDropdown) return;
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  };

  return (
    <View
      ref={rootRef}
      style={[styles.navItem]}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <TouchableOpacity
        style={[isActive && styles.activeNavItem]}
        onPress={() => {
          if (item.externalUrl) {
            // Open external URL in new tab
            if (Platform.OS === 'web') {
              window.open(item.externalUrl, '_blank');
            }
          } else {
            navigation.navigate(item.screen);
          }
        }}
      >
        <Text style={[styles.navText, isActive && styles.activeNavText]}>
          {item.name}
        </Text>
      </TouchableOpacity>
      {isDropdown && open && (
        <View style={styles.dropdown} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {dropdownItems.map((d) => (
            <TouchableOpacity key={d.name} style={styles.dropdownItem} onPress={() => navigation.navigate(d.screen)}>
              <Text style={styles.dropdownItemText}>{d.name}</Text>
            </TouchableOpacity>
          ))}
          {item.screen === 'CalculatorsHome' && (
            <TouchableOpacity style={styles.dropdownFooter} onPress={() => navigation.navigate('CalculatorsHome')}>
              <Text style={styles.dropdownFooterText}>View all calculators →</Text>
            </TouchableOpacity>
          )}
          {item.screen === 'ToolsHome' && (
            <TouchableOpacity style={styles.dropdownFooter} onPress={() => navigation.navigate('ToolsHome')}>
              <Text style={styles.dropdownFooterText}>View all tools →</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );
};

const UserMenu = ({ onDashboard }) => {
  const { colors } = useTheme();
  const { session, profile, signOut } = useAuth();
  const navigation = useNavigation();
  const [open, setOpen] = React.useState(false);
  const [loggingOut, setLoggingOut] = React.useState(false);
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    if (!open || Platform.OS !== 'web') return;
    const handleClick = (e) => {
      if (!rootRef.current) return;
      const el = rootRef.current;
      if (el && !el.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick, true);
    return () => document.removeEventListener('mousedown', handleClick, true);
  }, [open]);

  // Get display name - try profile first, then session user metadata, then email
  const userName = profile?.name
    || session?.user?.user_metadata?.name
    || session?.user?.email?.split('@')[0]
    || 'User';
  const displayName = userName.split(' ')[0]; // First name only

  const handleLogout = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setOpen(false);
    // signOut handles everything including redirect
    signOut();
  };

  return (
    <View ref={rootRef} style={{ position: 'relative', marginLeft: 16 }}>
      <TouchableOpacity
        style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, backgroundColor: colors.primary + '20' }}
        onPress={() => setOpen((o) => !o)}
      >
        <Text style={{ color: colors.text, fontWeight: '600', fontSize: 14 }}>
          Welcome, {displayName}
        </Text>
      </TouchableOpacity>
      {open && (
        <View style={{ position: 'absolute', top: 40, right: 0, backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border, borderRadius: 8, minWidth: 160, zIndex: 10001, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 4 }}>
          <TouchableOpacity style={{ paddingHorizontal: 12, paddingVertical: 10 }} onPress={() => { onDashboard(); setOpen(false); }}>
            <Text style={{ color: colors.text, fontSize: 14 }}>Dashboard</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{ paddingHorizontal: 12, paddingVertical: 10 }} onPress={() => {
            try { navigation.navigate('MyProfile'); } catch (_) { }
            setOpen(false);
          }}>
            <Text style={{ color: colors.text, fontSize: 14 }}>My Profile</Text>
          </TouchableOpacity>
          <View style={{ height: 1, backgroundColor: colors.border, marginVertical: 4 }} />
          <TouchableOpacity
            style={{ paddingHorizontal: 12, paddingVertical: 10, opacity: loggingOut ? 0.5 : 1 }}
            onPress={handleLogout}
            disabled={loggingOut}
          >
            <Text style={{ color: '#EF4444', fontSize: 14, fontWeight: '500' }}>
              {loggingOut ? 'Logging out...' : 'Logout'}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};